import React, { useState, useEffect } from "react";

import {
  FileText,
  Plus,
  Edit,
  Lock,
  FilePlus,
  CheckCircle,
  XCircle,
} from "lucide-react";

import Button from "../../../../Components/Button";
import DropdownSelector from "../../../../Components/DropdownSelector";
import CommonModal from "../../../../Components/Modal";
import SearchInput from "../../../../Components/Search/SearchInput";
import TabsComponent from "../../../../Components/Tab";
import TruncatedTooltip from "../../../../Components/TruncatedTooltip";
import { useAppDispatch, useAppSelector } from "../../../../Store/Hooks";
import {
  fetchAssessmentTemplates as fetchAdminAssessmentTemplates,
  updateAssessmentTemplateAdmin,
} from "../../../../Store/Reducers/AdminSlice";
import { fetchAssessmentTemplates } from "../../../../Store/Reducers/AssessmentSlice";
import { normalizeTeamAssessmentDisplayName } from "../../../../Utils/Helper/assessmentOriginHelpers";
import { formatDateUSShort } from "../../../../Utils/Helper/Helper";
import { toast } from "../../../../Utils/Helper/toast";
import TemplateForm from "../TemplateForm";

export interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  template_type: "izba_provided" | "custom";
  is_active: boolean;
  is_published: boolean;
  version: number;
  created_at: string;
  created_by: string;
  organization_id: string | null;
  questions: Question[];
}

export interface Question {
  id: string;
  question: string;
  type: "text" | "textarea" | "dropdown" | "multiple_choice" | "scale";
  required: boolean;
  options?: Array<{ value: string; label: string }> | null;
  order_index?: number;
}

interface TemplateListProps {
  maxShortTemplates: number | null;
  maxLongTemplates: number | null;
  isSuperAdmin: boolean;
  activeTab: "izba_provided" | "custom";
  onTabChange: (tab: "izba_provided" | "custom") => void;
  onEditTemplate?: (template: Template) => void;
  isAdmin?: boolean;
}

const TemplateList: React.FC<TemplateListProps> = ({
  maxShortTemplates,
  maxLongTemplates,
  isSuperAdmin,
  activeTab,
  onTabChange,
  onEditTemplate,
  isAdmin,
}) => {
  const dispatch = useAppDispatch();

  // Use AdminSlice for SuperAdmin, AssessmentSlice for others
  const assessmentState = useAppSelector(state => state.assessment);
  const adminState = useAppSelector((state: any) => state.admin);

  // AdminSlice stores templates as array directly, AssessmentSlice as object with templates property
  const reduxTemplates = isSuperAdmin
    ? {
        templates: Array.isArray(adminState.assessmentTemplates)
          ? adminState.assessmentTemplates
          : [],
      }
    : assessmentState.templates;
  const isLoading = isSuperAdmin
    ? adminState.templatesLoading || false
    : assessmentState.isLoading || false;

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [confirmPublishState, setConfirmPublishState] = useState<{
    isOpen: boolean;
    template: Template | null;
    isActivating: boolean;
  }>({
    isOpen: false,
    template: null,
    isActivating: true,
  });

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchAdminAssessmentTemplates() as any);
    } else {
      dispatch(fetchAssessmentTemplates({}));
    }
  }, [dispatch, isSuperAdmin]);

  // Transform Redux templates to local Template format
  const templates: Template[] = React.useMemo(() => {
    if (!reduxTemplates) return [];

    // Handle different response structures
    // AdminSlice returns: { templates: [] } or direct array
    // AssessmentSlice returns: { templates: [], total: number, limit: number, offset: number }
    // Old structure: { data: { templates: [] }, success: boolean }
    // Fallback: direct array
    const templatesData =
      reduxTemplates?.templates ||
      reduxTemplates?.data?.templates ||
      (Array.isArray(reduxTemplates) ? reduxTemplates : []);

    return (Array.isArray(templatesData) ? templatesData : [])
      .map((template: any) => {
        // Map question types from API format to component format
        const mapQuestionType = (
          questionType: string
        ): "text" | "textarea" | "dropdown" | "multiple_choice" | "scale" => {
          switch (questionType) {
            case "free_text":
              return "textarea";
            case "single_choice":
              return "dropdown";
            case "multiple_choice":
              return "multiple_choice";
            case "scale":
              return "scale";
            default:
              return "text";
          }
        };

        // Use type field directly
        const category = template.type || template.category || "short";

        // Determine template type: if is_global is true, it's Izba template, otherwise it's custom
        const template_type: "izba_provided" | "custom" =
          template.is_global === true ? "izba_provided" : "custom";

        // Transform questions from json_schema format
        const questions = (
          template.json_schema?.questions ||
          template.questions ||
          []
        )
          .map((q: any) => ({
            id: q.id,
            question: q.question_text || q.question || "",
            type: mapQuestionType(
              q.question_type || q.type || q.questionType || "text"
            ),
            required: q.required !== undefined ? q.required : true,
            options: q.options?.choices
              ? q.options.choices.map((choice: any) => ({
                  value: choice.value,
                  label: choice.label,
                }))
              : q.options || null,
            order_index: q.order_index || q.display_order || 0,
          }))
          .sort(
            (a: any, b: any) => (a.order_index || 0) - (b.order_index || 0)
          );

        return {
          id: template.template_id || template.id,
          name: template.name || "",
          description: template.description || "",
          category: category,
          template_type,
          is_active:
            template.is_active !== undefined ? template.is_active : true,
          is_published:
            template.is_active !== undefined ? template.is_active : true,
          version: template.version || 1,
          created_at: template.created_at || template.createdAt || "",
          created_by:
            typeof template.created_by === "string"
              ? template.created_by
              : template.created_by?.id ||
                template.createdBy?.id ||
                template.createdBy ||
                "",
          organization_id: template.organization_id || template.org_id || null,
          questions,
        };
      })
      .sort((a: Template, b: Template) => {
        if (
          a.template_type === "izba_provided" &&
          b.template_type === "custom"
        ) {
          return -1;
        }
        if (
          a.template_type === "custom" &&
          b.template_type === "izba_provided"
        ) {
          return 1;
        }
        return 0;
      });
  }, [reduxTemplates]);

  const hasReachedLimit = () => {
    const shortTemplates = templates.filter(t => t.category === "short").length;
    const longTemplates = templates.filter(t => t.category === "long").length;

    const shortLimitReached =
      maxShortTemplates !== null && shortTemplates >= maxShortTemplates;
    const longLimitReached =
      maxLongTemplates !== null && longTemplates >= maxLongTemplates;

    return shortLimitReached && longLimitReached;
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filterCategory === "all" || template.category === filterCategory;
    const matchesActiveTab = template.template_type === activeTab;
    return matchesSearch && matchesCategory && matchesActiveTab;
  });

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateForm(true);
  };

  const handleEditTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setShowTemplateForm(true);
    if (onEditTemplate) {
      onEditTemplate(template);
    }
  };

  const handleCloseTemplateForm = () => {
    setShowTemplateForm(false);
    setSelectedTemplate(null);
  };

  const handleTemplateFormSuccess = () => {
    if (isSuperAdmin) {
      dispatch(fetchAdminAssessmentTemplates() as any);
    } else {
      dispatch(fetchAssessmentTemplates({}));
    }
  };

  const handleTogglePublish = async (template: Template, isActive: boolean) => {
    if (!isSuperAdmin) return;
    try {
      await dispatch(
        updateAssessmentTemplateAdmin({
          id: template.id,
          data: { is_active: isActive },
        }) as any
      ).unwrap();
      toast.success(
        isActive
          ? "Template published successfully!"
          : "Template unpublished successfully!"
      );
      dispatch(fetchAdminAssessmentTemplates() as any);
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        (isActive
          ? "Failed to publish template."
          : "Failed to unpublish template.");
      toast.error(errorMessage);
    }
  };

  const openConfirmPublishModal = (
    template: Template,
    isActivating: boolean
  ) => {
    if (!isSuperAdmin) return;
    setConfirmPublishState({
      isOpen: true,
      template,
      isActivating,
    });
  };

  const closeConfirmPublishModal = () => {
    setConfirmPublishState(prev => ({
      ...prev,
      isOpen: false,
    }));
  };

  const handleConfirmPublish = async () => {
    if (!confirmPublishState.template) return;
    await handleTogglePublish(
      confirmPublishState.template,
      confirmPublishState.isActivating
    );
    closeConfirmPublishModal();
  };

  const getStatusBadgeColor = (template: Template) => {
    if (!template.is_active) return "bg-gray-100 text-gray-800";
    if (template.is_published) return "bg-green-100 text-green-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const getStatusText = (template: Template) => {
    if (!template.is_active) return "Inactive";
    if (template.is_published) return "Published";
    return "Draft";
  };

  return (
    <div className="w-full p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            Assessment Templates
          </h2>
          <p className="text-sm text-gray-500">
            Manage assessment templates for organizations
          </p>
        </div>
        <div className="flex-shrink-0">
          {(isSuperAdmin || isAdmin) && !hasReachedLimit() && (
            <Button
              variant="primary"
              onClick={handleCreateTemplate}
              // className="btn-lg"
              icon={<Plus className="h-4 w-4" />}
            >
              Create Template
            </Button>
          )}
          {(isSuperAdmin || isAdmin) && hasReachedLimit() && (
            <div className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-500 bg-gray-50 cursor-not-allowed">
              <Lock className="h-4 w-4 mr-2" />
              Template Limit Reached
            </div>
          )}
        </div>
      </div>

      {/* Template Type Tabs and Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        {!isSuperAdmin && (
          <div className="flex-shrink-0">
            <TabsComponent
              tabs={[
                {
                  id: "izba_provided",
                  label: "Izba Provided",
                  icon: <FileText className="h-4 w-4" />,
                },
                {
                  id: "custom",
                  label: "Custom",
                  icon: <FilePlus className="h-4 w-4" />,
                },
              ]}
              activeTab={activeTab}
              onTabChange={(tabId: string) =>
                onTabChange(tabId as "izba_provided" | "custom")
              }
              variant="tab-underline"
            />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-1 lg:justify-end">
          <div className="flex-1 max-w-md">
            <SearchInput
              placeholder="Search templates..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex-shrink-0">
            <DropdownSelector
              label="All Categories"
              selected={filterCategory}
              options={[
                { label: "All Categories", value: "all" },
                { label: "Short Assessment", value: "short" },
                { label: "Long Assessment", value: "long" },
              ]}
              onSelect={setFilterCategory}
            />
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full flex flex-col items-center justify-center py-12 min-h-[400px]">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-200 border-t-purple-600 mb-4"></div>
            <p className="text-gray-600 text-sm">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-12 min-h-[400px]">
            <FileText className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No templates found
            </h3>
            <p className="text-gray-500 text-sm text-center max-w-md">
              {searchTerm || filterCategory !== "all"
                ? "Try adjusting your search or filter criteria"
                : "Create your first assessment template to get started"}
            </p>
          </div>
        ) : (
          filteredTemplates.map(template => (
            <div
              key={template.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg hover:border-purple-200 transition-all duration-200 overflow-hidden flex flex-col h-full min-w-0"
            >
              {/* Card Content */}
              <div className="p-5 flex flex-col flex-1 min-w-0">
                {/* Template Header with Badges */}
                <div className="mb-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 leading-tight flex-1 line-clamp-2 min-h-[2.5rem]">
                      {normalizeTeamAssessmentDisplayName(template.name)}
                    </h3>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getStatusBadgeColor(template)}`}
                      >
                        {getStatusText(template)}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                          template.category === "short"
                            ? "bg-blue-50 text-blue-700 border border-blue-200"
                            : template.category === "long"
                              ? "bg-purple-50 text-purple-700 border border-purple-200"
                              : "bg-orange-50 text-orange-700 border border-orange-200"
                        }`}
                      >
                        {template.category.charAt(0).toUpperCase() +
                          template.category.slice(1)}
                      </span>
                    </div>
                  </div>

                  {/* Description - constrained so long text doesn't overflow */}
                  <div className="min-w-0 overflow-hidden">
                    {template.description &&
                    template.description.trim() &&
                    template.description.length > 3 ? (
                      <TruncatedTooltip
                        tooltip={template.description}
                        className="text-sm text-gray-600 leading-relaxed"
                      >
                        {template.description}
                      </TruncatedTooltip>
                    ) : (
                      <p className="text-sm text-gray-400 italic line-clamp-2">
                        No description available
                      </p>
                    )}
                  </div>
                </div>

                {/* Template Info Grid */}
                <div className="bg-gray-50 rounded-lg p-3 flex-shrink-0">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex flex-col">
                      <span className="text-gray-500 text-xs mb-1">
                        Questions
                      </span>
                      <span className="font-medium text-gray-900">
                        {template.questions.length}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-500 text-xs mb-1">Type</span>
                      <span className="font-medium text-gray-900">
                        {template.template_type === "izba_provided"
                          ? "Izba Provided"
                          : "Custom"}
                      </span>
                    </div>
                    <div className="flex flex-col col-span-2">
                      <span className="text-gray-500 text-xs mb-1">
                        Created
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatDateUSShort(template.created_at, true)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions - Always reserve space */}
                {template.template_type === "custom" || isSuperAdmin ? (
                  <div className="mt-auto flex-shrink-0 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-end gap-0">
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-150"
                        title="Edit Template"
                      >
                        <Edit className="h-4 w-4 shrink-0" />
                        <span>Edit</span>
                      </button>
                      {isSuperAdmin && (
                        <>
                          <span
                            className="h-4 w-px bg-gray-300 shrink-0 mx-0.5"
                            aria-hidden
                          />
                          {template.is_active ? (
                            <button
                              onClick={() =>
                                openConfirmPublishModal(template, false)
                              }
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-amber-600 hover:bg-amber-50 transition-colors duration-150"
                              title="Unpublish Template"
                            >
                              <XCircle className="h-4 w-4 shrink-0" />
                              <span>Unpublish</span>
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                openConfirmPublishModal(template, true)
                              }
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-green-600 hover:bg-green-50 transition-colors duration-150"
                              title="Publish Template"
                            >
                              <CheckCircle className="h-4 w-4 shrink-0" />
                              <span>Publish</span>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Template Form Modal */}
      <TemplateForm
        isOpen={showTemplateForm}
        onClose={handleCloseTemplateForm}
        template={selectedTemplate}
        activeTab={activeTab}
        onSuccess={handleTemplateFormSuccess}
        isSuperAdmin={isSuperAdmin}
      />
      <CommonModal
        isOpen={confirmPublishState.isOpen}
        toggle={closeConfirmPublishModal}
        title={
          confirmPublishState.isActivating
            ? "Publish Template"
            : "Unpublish Template"
        }
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Are you sure you want to{" "}
            <span className="font-semibold">
              {confirmPublishState.isActivating ? "publish" : "unpublish"}
            </span>{" "}
            the template{" "}
            <span className="font-semibold">
              {confirmPublishState.template?.name || "this template"}
            </span>
            ?
          </p>
          <p className="text-xs text-gray-500">
            {confirmPublishState.isActivating
              ? "Published templates will be available for use in assessments."
              : "Unpublished templates will no longer be available for new assessments, but existing assessments will not be affected."}
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeConfirmPublishModal}>
              Cancel
            </Button>
            <Button
              variant={confirmPublishState.isActivating ? "primary" : "danger"}
              onClick={handleConfirmPublish}
            >
              {confirmPublishState.isActivating ? "Publish" : "Unpublish"}
            </Button>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default TemplateList;
